// Slice-10 i18n. Stubbed dictionary loader for en-US + es so the SPA
// has the seam in place without taking a dependency on i18next /
// FormatJS yet. Slice 11 swaps the dictionary loader for the real
// FormatJS pipeline when the message-catalogue tooling is wired.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import enUS from "./messages/en-US.json";
import es from "./messages/es.json";

type Locale = "en-US" | "es";
type Messages = Record<string, string>;

const DICTIONARIES: Record<Locale, Messages> = {
  "en-US": enUS,
  es: es,
};

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  /** Returns the localised string for `key`; falls back to the key if missing. */
  t: (key: string) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }): JSX.Element {
  const [locale, setLocale] = useState<Locale>(() => {
    const stored = window.localStorage.getItem("rollcall.locale");
    if (stored === "en-US" || stored === "es") {
      return stored;
    }
    const fromBrowser = window.navigator.language;
    return fromBrowser.startsWith("es") ? "es" : "en-US";
  });

  const persistAndSet = useCallback((next: Locale) => {
    window.localStorage.setItem("rollcall.locale", next);
    setLocale(next);
  }, []);

  const t = useCallback(
    (key: string) => DICTIONARIES[locale][key] ?? DICTIONARIES["en-US"][key] ?? key,
    [locale],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale, setLocale: persistAndSet, t }),
    [locale, persistAndSet, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const value = useContext(I18nContext);
  if (value === null) {
    throw new Error("useI18n must be called inside <I18nProvider>");
  }
  return value;
}
